import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { IconCamera, IconCheck, IconChevronLeft, IconNavigation, IconPhone, IconTruck, IconQrcode, IconAlertCircle, IconPlayerPlay, IconSnowflake, IconBell } from '@tabler/icons-react';
import { Modal, StateView, useToast, type ScreenState } from '@hana/ui';
import { DRIVER, STEPS } from './trips';
import { confirmLabel, findTrip, fmtDev, gpsLine, JOBS, mapUrl, missing, NOTES, reqDone, reqsOf, RF_READ, RF_TOL, tempAlerts, tempDev, TIMES } from './tripdetailData';
import { useT } from '@hana/i18n';
import s from './tripdetail.module.css';
import './tripdetail.i18n';

const PREV = [['photo', 'mvt.prevPhoto', 'mvt.prevShoot'], ['cost', 'mvt.prevCost', 'mvt.prevEnter']] as const;
const STATES: ScreenState[] = ['ready', 'loading', 'empty', 'error', 'noperm'];
function param(k: string): string | null { return new URLSearchParams(location.search).get(k); }
function forcedState(): ScreenState {
  const v = param('state');
  return STATES.includes(v as ScreenState) ? (v as ScreenState) : 'ready';
}

/** Mint-Mov-Phone khung 2 "Chi tiết chuyến · 7 bước GPS" (`/mov/trip?id=`). Thao tác giả lập trong bộ nhớ, toast có Hoàn tác. Thanh tab dưới do shell Mov lo. */
export function TripDetail() {
  const state = forcedState();
  const toast = useToast();
  const t = useT();
  const trip = findTrip(param('id'))!;
  // Cont lạnh: nhiệt độ yêu cầu của chuyến; `?rf=-18` để mô phỏng (dữ liệu mẫu board không có chuyến lạnh).
  const rfParam = param('rf');
  const rf = trip.rf ?? (rfParam !== null && rfParam !== '' && Number.isFinite(Number(rfParam)) ? Number(rfParam) : undefined);
  const rfK = rf === undefined ? '' : `${rf}°C`;
  const [p, setP] = useState(trip.p0);
  const [vals, setVals] = useState<Record<string, string | boolean>>({});
  const [qr, setQr] = useState<null | 'ok' | 'bad'>(null);
  const [prevDone, setPrevDone] = useState<Record<string, boolean>>({});
  const [times, setTimes] = useState<Record<number, string>>({});
  const set = (k: string, v: string | boolean) => setVals((o) => ({ ...o, [k]: v }));

  const confirm = (i: number) => {
    const miss = missing(i, vals, rf);
    if (miss.length) { toast({ msg: t('mvt.missing', { l: miss.join(', ') }) }); return; }
    const prev = p;
    const d = new Date();
    const hm = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    setP(i + 1);
    setTimes((o) => ({ ...o, [i]: hm }));
    const dev = tempAlerts(i, vals, rf).length > 0;
    toast({ msg: dev ? t('mvt.confDev', { s: STEPS[i]! }) : t('mvt.conf', { s: STEPS[i]!, hm }), undo: () => setP(prev) });
  };

  return (
    <div className={s.page}>
      <div className={s.hd}>
        <Link to="/" className={s.ib} aria-label={t('mvt.back')}><IconChevronLeft size={26} stroke={2.2} aria-hidden="true" /></Link>
        <div><b>{trip.no}</b><small>{JOBS[trip.id]} · {trip.cus}</small></div>
        <a className={s.ib} href={`tel:${DRIVER.dutyPhone}`} aria-label={t('mvt.callDuty')}><IconPhone size={22} stroke={2} aria-hidden="true" /></a>
      </div>
      {state === 'error' && <div role="status" className={s.net}><b>{t('mvt.offB')}</b> {t('mvt.offT')}</div>}
      <StateView
        state={state === 'error' ? 'ready' : state}
        title={state === 'noperm' ? t('mvt.npTitle') : t('mvt.noneTitle')}
        desc={state === 'noperm' ? t('mvt.npDesc') : t('mvt.noneDesc')}
        icon={<IconTruck size={28} stroke={1.6} aria-hidden="true" />}
        skeleton={<>{[1, 2, 3, 4, 5].map((k) => <div key={k} className={s.sk} aria-hidden="true" />)}</>}
      >
        {trip.id === 'y12' && (
          <section aria-label={t('mvt.prevTitle')} className={s.prev}>
            <b>{t('mvt.prevTitle')}</b>
            {PREV.map(([k, label, btn]) => {
              const done = !!prevDone[k];
              return (
                <div key={k} className={s.prevRow}>
                  <span className={s.prevDot} data-ok={done ? '1' : '0'}>{done ? <IconCheck size={14} stroke={3} aria-hidden="true" /> : '!'}</span>
                  <span className={s.prevK}>{t(label)}</span>
                  <button type="button" className={s.prevBtn} disabled={done} data-done={done ? '1' : '0'}
                    onClick={() => { setPrevDone((o) => ({ ...o, [k]: true })); toast({ msg: t(k === 'photo' ? 'mvt.prevPhotoOk' : 'mvt.prevCostOk'), undo: () => setPrevDone((o) => { const n = { ...o }; delete n[k]; return n; }) }); }}>
                    {done ? t('mvt.prevHave') : t(btn)}
                  </button>
                </div>
              );
            })}
            <div className={s.prevNote}>{t('mvt.prevNote')}</div>
          </section>
        )}
        {rf !== undefined && (
          <div className={s.rf}>
            <IconSnowflake size={24} stroke={2} aria-hidden="true" />
            <div><b>{t('mvt.rf', { k: rfK })}</b> · {t('mvt.vent')} · <span title={t('mvt.rfTip', { n: RF_TOL })}><IconBell size={15} stroke={1.8} aria-hidden="true" />±{RF_TOL}°C</span></div>
          </div>
        )}
        <div className={s.card}>
          {STEPS.map((k, i) => {
            const st = i < p ? 'done' : i === p ? 'cur' : 'todo';
            const reqs = st === 'cur' ? reqsOf(i, rf) : [];
            const miss = missing(i, vals, rf);
            const tm = st === 'done' ? (times[i] ?? TIMES[trip.id]?.[i] ?? '—') : '';
            return (
              <div key={k} className={s.step}>
                <span className={s.dot} data-s={st}>{st === 'done' ? <IconCheck size={16} stroke={3} aria-hidden="true" /> : i + 1}</span>
                <div className={s.body}>
                  <div className={s.top}><b data-s={st}>{k}</b><span>{tm}</span></div>
                  {st === 'done' && <div className={s.src}>{times[i] || !TIMES[trip.id]?.[i] ? t('mvt.byDriver') : t('mvt.byGps')}</div>}
                  {st === 'cur' && (
                    <div className={s.cur}>
                      <div className={s.gps}>{gpsLine(i, trip.id)}</div>
                      {reqs.map((r) => (
                        <div key={r.key} className={s.req}>
                          <div className={s.reqHd}><span className={s.rd} data-ok={reqDone(r, vals[r.key], vals) ? '1' : '0'}>{reqDone(r, vals[r.key], vals) ? <IconCheck size={14} stroke={3} aria-hidden="true" /> : <IconAlertCircle size={14} stroke={2.4} aria-hidden="true" />}</span>{r.label}</div>
                          {r.kind === 'in' ? (
                            <input className={s.in} aria-label={r.label} placeholder={r.ph} value={String(vals[r.key] ?? '')} onChange={(e) => set(r.key, e.target.value.toUpperCase())} />
                          ) : (
                            <>
                              {r.kind === 'temp' && (
                                <label className={s.temp}>
                                  <span>{t('mvt.tempOn')}</span>
                                  <input className={s.tin} inputMode="decimal" aria-label={t('mvt.tempRead')} value={String(vals[r.key] ?? '')} onChange={(e) => set(r.key, e.target.value)} />
                                  <span>°C</span>
                                </label>
                              )}
                              {r.kind === 'temp' && rf !== undefined && tempDev(vals[r.key], rf) !== null && (() => {
                                const dv = tempDev(vals[r.key], rf)!;
                                const bad = dv > RF_TOL;
                                return <div role="alert" className={s.tw} data-bad={bad ? '1' : '0'}>{bad ? t('mvt.devBad', { d: fmtDev(dv), k: rfK }) : t('mvt.devOk', { d: fmtDev(dv), n: RF_TOL })}</div>;
                              })()}
                              <button type="button" className={s.ghost} onClick={() => {
                                if (r.kind === 'qr') { setQr('ok'); return; }
                                if (r.kind === 'temp') { const v = String(vals[r.key] || RF_READ); setVals((o) => ({ ...o, [r.key + 'p']: true, [r.key]: v })); toast({ msg: t('mvt.aiTemp', { v }) }); return; }
                                set(r.key, true); toast({ msg: t('mvt.photoOk') });
                              }}>
                                {r.kind === 'qr' ? <IconQrcode size={20} aria-hidden="true" /> : <IconCamera size={20} aria-hidden="true" />}
                                {r.kind === 'qr' ? (vals[r.key] ? t('mvt.scanned') : t('mvt.scanQr')) : r.kind === 'temp' ? (vals[r.key + 'p'] ? t('mvt.tempRetake') : t('mvt.tempShot')) : vals[r.key] ? t('mvt.photoRetake') : t('mvt.shoot')}
                              </button>
                            </>
                          )}
                        </div>
                      ))}
                      {miss.length > 0 && <div className={s.warn}>{t('mvt.missing', { l: miss.join(', ') })}</div>}
                      <button type="button" className={s.btn} aria-disabled={miss.length > 0} onClick={() => confirm(i)}>{confirmLabel(i, trip.id)}</button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <div className={s.card}>
          <h3>{t('mvt.factsTitle')}</h3>
          {([[t('mvt.contNo'), String(vals.cont || '') || t('mvt.empty'), !vals.cont], [t('mvt.sealNo'), String(vals.seal || '') || t('mvt.empty'), !vals.seal], [t('mvt.mooc'), vals.qr ? t('mvt.moocScanned') : t('mvt.moocNot', { m: DRIVER.mooc }), !vals.qr], [t('mvt.contType'), trip.cont, false]] as const).map(([k, v, w]) => (
            <div key={k} className={s.fact}><span>{k}</span><b data-w={w ? '1' : '0'}>{v}</b></div>
          ))}
          <div className={s.pad}><button type="button" className={s.ghost} style={{ width: '100%' }} onClick={() => setQr('ok')}><IconQrcode size={20} aria-hidden="true" />{t('mvt.scanQr')}</button></div>
        </div>
        <div className={s.card}>
          <h3>{t('mvt.points')}</h3>
          {trip.pts.map(([k, v]) => (
            <div key={k} className={s.pt}>
              <div><small>{k}</small><div>{v}</div></div>
              <a className={s.go} href={mapUrl(v)} target="_blank" rel="noopener noreferrer" aria-label={t('mvt.navTo', { v })}><IconNavigation size={22} aria-hidden="true" /></a>
            </div>
          ))}
        </div>
        <div className={s.note}><small>{t('mvt.dutyNote')} · {DRIVER.duty}</small><div>{NOTES[trip.id]}</div></div>
      </StateView>
      <Modal
        open={qr !== null}
        onOpenChange={(o) => !o && setQr(null)}
        title={t('mvt.scanQr')}
      >
        <div className={s.qrBox}>
          <span className={s.qrFrame} />
          <span className={s.qrCap}>{t('mvt.qrCap')}</span>
        </div>
        <div className={s.qrAssign}>{t('mvt.assigned')} <b>51R-093.62</b></div>
        {qr === 'bad' && <div role="alert" className={s.qrBad}>{t('mvt.qrBad')}</div>}
        <button type="button" className={s.btn} onClick={() => { set('qr', true); setQr(null); toast({ msg: t('mvt.qrOk') }); }}><IconPlayerPlay size={16} stroke={1.8} aria-hidden="true" />{t('mvt.simOk')}</button>
        <button type="button" className={s.ghost} onClick={() => setQr('bad')}><IconPlayerPlay size={16} stroke={1.8} aria-hidden="true" />{t('mvt.simBad')}</button>
      </Modal>
    </div>
  );
}
